import { exportToCSV, type ExportColumn } from "@/lib/export";

export interface ClientReport {
  clientName: string;
  periodStart: number | null;
  periodEnd: number;
  deliveredOnly: boolean;
  filteredProducts: boolean;
  rows: Array<{
    origin: string;
    product: string;
    pounds: number;
    averagePricePerPound: number;
  }>;
}

export function reportDate(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

// Quoting CSV cells alone does not stop spreadsheet formula execution.
function spreadsheetText(value: unknown): string {
  const text = String(value);
  return /^[\s]*[=+@-]|^[\t\r\n]/.test(text) ? `'${text}` : text;
}

export const CLIENT_REPORT_COLUMNS: ExportColumn[] = [
  { key: "client", label: "Client", format: spreadsheetText },
  { key: "period", label: "Period (UTC)" },
  { key: "generatedAt", label: "Generated at (UTC)" },
  { key: "scope", label: "Purchases included" },
  { key: "priceBasis", label: "Price basis" },
  { key: "origin", label: "Origin", format: spreadsheetText },
  { key: "product", label: "Product", format: spreadsheetText },
  { key: "pounds", label: "Pounds", format: (value) => Number(value).toFixed(2) },
  { key: "averagePricePerPound", label: "Average price per pound (USD)", format: (value) => Number(value).toFixed(2) },
];

export function clientReportPeriod(report: ClientReport): string {
  return `${report.periodStart === null ? "All time" : reportDate(report.periodStart)} through ${reportDate(report.periodEnd)}`;
}

export function clientReportGeneratedAt(report: ClientReport): string {
  return new Date(report.periodEnd).toISOString().replace("T", " ").replace(/\.\d{3}Z$/, " UTC");
}

export function clientReportScope(report: ClientReport): string {
  return `${report.deliveredOnly ? "Delivered orders" : "Completed / in-progress orders"}${report.filteredProducts ? "; selected products only" : ""}`;
}

export function clientReportExportRows(report: ClientReport) {
  return report.rows.map((row) => ({
    client: report.clientName,
    period: clientReportPeriod(report),
    generatedAt: clientReportGeneratedAt(report),
    scope: clientReportScope(report),
    priceBasis: "Pound-weighted recorded item price; before order-level discounts, freight, and tax",
    origin: row.origin,
    product: row.product,
    pounds: row.pounds,
    averagePricePerPound: row.averagePricePerPound,
  }));
}

export function downloadClientReport(report: ClientReport): void {
  const clientSlug = report.clientName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  exportToCSV(
    clientReportExportRows(report),
    CLIENT_REPORT_COLUMNS,
    `client-report-${clientSlug}-${report.periodStart === null ? "all-time" : reportDate(report.periodStart)}-to-${reportDate(report.periodEnd)}.csv`,
  );
}
